import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

import { readAudioBarOffset } from '@/audioOverview/bar/audioBarOffset';

/** Top-left of the shell window, in viewport pixels. */
export interface ShellPosition {
  x: number;
  y: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * The window's smallest size (ORA-649), and the shape everything else is
 * derived from. The window opens at `maxShellSize` instead (ORA-696); this is
 * the floor the student can shrink back down to.
 */
export const SHELL_WINDOW_WIDTH = 400;
export const SHELL_WINDOW_HEIGHT = 640;
/** Distance the bubble and the window keep from the viewport edges. */
export const SHELL_INSET = 24;

/**
 * Height per unit of width. Resizing is aspect-locked to this shape, so only
 * the expanded overlay ever shows the conversation at another ratio.
 */
export const SHELL_ASPECT_RATIO = SHELL_WINDOW_HEIGHT / SHELL_WINDOW_WIDTH;

/** Size the student resized the window to. Null means `maxShellSize`. */
export interface ShellSize {
  width: number;
  height: number;
}

export const SHELL_POSITION_STORAGE_KEY = 'ora.companionShell.position';
export const SHELL_SIZE_STORAGE_KEY = 'ora.companionShell.size';

/** Mirrors `--z-companion-shell` in `Base/_colors.scss`. */
export const SHELL_Z_INDEX = 1100;

/**
 * How Intercom is booted for a student on the shell (ORA-646). Its launcher
 * stands down — the bubble is the single entry point — and the Messenger opens
 * in the shell's own corner, at the shell's own layer, so the two panels swap
 * in place instead of stacking. Kept beside the geometry they have to match.
 */
export const SHELL_INTERCOM_SETTINGS = {
  hide_default_launcher: true,
  alignment: 'right',
  horizontal_padding: SHELL_INSET,
  vertical_padding: SHELL_INSET,
  z_index: SHELL_Z_INDEX,
} as const satisfies Partial<IntercomSettings>;

/**
 * The area the window may use: the browser viewport above the docked Audio
 * Overview bar (ORA-735), whose height the stylesheet also adds to the
 * window's bottom inset.
 */
export const currentShellViewport = (): Viewport => ({
  width: window.innerWidth,
  height: Math.max(0, window.innerHeight - readAudioBarOffset()),
});

const currentViewport = currentShellViewport;

/**
 * The biggest aspect-locked window the viewport can hold with the inset kept
 * on all four sides, and the size an unsized window opens at (ORA-696). On a
 * short viewport this is smaller than the minimum, which is what lets
 * `clampShellSize` shrink below it rather than hand back a window that cannot
 * fit.
 */
export const maxShellSize = (
  viewport: Viewport = currentViewport()
): ShellSize => {
  const width = Math.max(
    0,
    Math.min(
      viewport.width - SHELL_INSET * 2,
      (viewport.height - SHELL_INSET * 2) / SHELL_ASPECT_RATIO
    )
  );

  // Floored, not rounded: a rounded-up width would derive a height a pixel
  // taller than the viewport can actually hold.
  const floored = Math.floor(width);

  return {
    width: floored,
    height: Math.round(floored * SHELL_ASPECT_RATIO),
  };
};

/**
 * What the window actually measures: a stored size shrinks to fit a short
 * viewport (`max-height` in the stylesheet), so clamping against the stored
 * 640px would leave a gap under it on a laptop; no stored size means the
 * window opened at `maxShellSize`, which already fits.
 */
export const shellWindowSize = (
  viewport: Viewport = currentViewport(),
  size: ShellSize | null = null
): ShellSize => {
  if (!size) return maxShellSize(viewport);

  return {
    width: Math.min(size.width, viewport.width - SHELL_INSET * 2),
    height: Math.min(size.height, viewport.height - SHELL_INSET * 2),
  };
};

/**
 * Aspect-locks a width and holds it between the minimum size and the viewport;
 * the height always follows from the width, so only the width is asked for.
 */
export const clampShellSize = (
  size: { width: number },
  viewport: Viewport = currentViewport()
): ShellSize => {
  const max = maxShellSize(viewport);
  const min = Math.min(SHELL_WINDOW_WIDTH, max.width);
  const width = Math.round(Math.min(Math.max(size.width, min), max.width));

  return { width, height: Math.round(width * SHELL_ASPECT_RATIO) };
};

/**
 * Keeps the window fully on screen. A position saved on a wide monitor would
 * otherwise come back off-screen on a laptop, with no way to drag it home.
 */
export const clampShellPosition = (
  position: ShellPosition,
  viewport: Viewport = currentViewport(),
  size: ShellSize | null = null
): ShellPosition => {
  const { width, height } = shellWindowSize(viewport, size);
  const maxX = Math.max(0, viewport.width - width);
  const maxY = Math.max(0, viewport.height - height);

  return {
    x: Math.min(Math.max(Math.round(position.x), 0), maxX),
    y: Math.min(Math.max(Math.round(position.y), 0), maxY),
  };
};

const isShellPosition = (value: unknown): value is ShellPosition =>
  typeof value === 'object' &&
  value !== null &&
  Number.isFinite((value as ShellPosition).x) &&
  Number.isFinite((value as ShellPosition).y);

/**
 * The position is a per-device convenience, so every storage access is
 * best-effort: a private window, cleared site data or a browser blocking
 * storage simply means the window opens where it always does.
 */
export const loadShellPosition = (): ShellPosition | null => {
  try {
    const raw = window.localStorage.getItem(SHELL_POSITION_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    return isShellPosition(parsed) ? { x: parsed.x, y: parsed.y } : null;
  } catch {
    return null;
  }
};

export const saveShellPosition = (position: ShellPosition): void => {
  try {
    window.localStorage.setItem(
      SHELL_POSITION_STORAGE_KEY,
      JSON.stringify(position)
    );
  } catch {
    // Ignored: see loadShellPosition.
  }
};

export const clearShellPosition = (): void => {
  try {
    window.localStorage.removeItem(SHELL_POSITION_STORAGE_KEY);
  } catch {
    // Ignored: see loadShellPosition.
  }
};

/**
 * Only the width is stored: the height follows from the locked aspect ratio,
 * so a stored pair could never disagree with it. Best-effort like the
 * position above.
 */
export const loadShellSize = (): ShellSize | null => {
  try {
    const raw = window.localStorage.getItem(SHELL_SIZE_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    const width = (parsed as { width?: unknown } | null)?.width;
    if (typeof width !== 'number' || !Number.isFinite(width)) return null;

    return { width, height: Math.round(width * SHELL_ASPECT_RATIO) };
  } catch {
    return null;
  }
};

export const saveShellSize = (size: ShellSize): void => {
  try {
    window.localStorage.setItem(
      SHELL_SIZE_STORAGE_KEY,
      JSON.stringify({ width: size.width })
    );
  } catch {
    // Ignored: see loadShellPosition.
  }
};

export const clearShellSize = (): void => {
  try {
    window.localStorage.removeItem(SHELL_SIZE_STORAGE_KEY);
  } catch {
    // Ignored: see loadShellPosition.
  }
};

interface PointerLike {
  button?: number;
  pointerId?: number;
  clientX: number;
  clientY: number;
  target: EventTarget | null;
  currentTarget?: EventTarget | null;
  preventDefault?: () => void;
}

/**
 * How far the pointer must travel before a press becomes a drag. Without it a
 * plain click on the header would commit — and persist — a position.
 */
const DRAG_THRESHOLD_PX = 3;

/**
 * Controls that must keep working while they sit on the drag handle. A press
 * that starts on one of them is a click, not a drag.
 */
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="menuitem"], [data-no-drag]';

export interface UseDragPositionOptions {
  /** The window element, used to read where an unpositioned window starts. */
  elementRef: RefObject<HTMLElement | null>;
  /** The committed position, or null while the window sits at its default. */
  position: ShellPosition | null;
  /** The committed size, or null while the window is at its default size. */
  size?: ShellSize | null;
  /** Called once, with the clamped position, when the drag ends. */
  onCommit: (position: ShellPosition) => void;
  enabled?: boolean;
}

export interface UseDragPositionResult {
  /** Spread onto the drag handle (the window header). */
  dragHandleProps: { onPointerDown: (event: PointerLike) => void };
  /** The live position during a drag; null when the drag is not moving. */
  dragPosition: ShellPosition | null;
  isDragging: boolean;
}

/**
 * Pointer-events drag for the shell window. Deliberately hand-rolled: this is
 * one draggable element with no drop targets, sorting or accessibility tree to
 * maintain, which is a fraction of what a drag library brings in.
 */
export const useDragPosition = ({
  elementRef,
  position,
  size = null,
  onCommit,
  enabled = true,
}: UseDragPositionOptions): UseDragPositionResult => {
  const [dragPosition, setDragPosition] = useState<ShellPosition | null>(null);
  const [isPointerDown, setIsPointerDown] = useState(false);

  const dragRef = useRef<{
    originX: number;
    originY: number;
    startX: number;
    startY: number;
    pointerId?: number;
    hasMoved: boolean;
  } | null>(null);

  const latestRef = useRef<ShellPosition | null>(null);
  const onCommitRef = useRef(onCommit);
  const sizeRef = useRef(size);

  useEffect(() => {
    onCommitRef.current = onCommit;
    sizeRef.current = size;
  }, [onCommit, size]);

  useEffect(() => {
    if (!isPointerDown) return undefined;

    const handleMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (drag.pointerId !== undefined && event.pointerId !== drag.pointerId) {
        return;
      }

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      if (!drag.hasMoved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      drag.hasMoved = true;

      const next = clampShellPosition(
        { x: drag.originX + dx, y: drag.originY + dy },
        currentViewport(),
        sizeRef.current
      );

      latestRef.current = next;
      setDragPosition(next);
    };

    const handleUp = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (
        drag?.pointerId !== undefined &&
        event.pointerId !== undefined &&
        event.pointerId !== drag.pointerId
      ) {
        return;
      }

      const committed = drag?.hasMoved ? latestRef.current : null;

      dragRef.current = null;
      latestRef.current = null;
      setIsPointerDown(false);
      setDragPosition(null);
      if (committed) {
        onCommitRef.current(
          clampShellPosition(committed, currentViewport(), sizeRef.current)
        );
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [isPointerDown]);

  const onPointerDown = useCallback(
    (event: PointerLike) => {
      if (!enabled) return;
      if (event.button !== undefined && event.button !== 0) return;

      const target = event.target as HTMLElement | null;
      if (target?.closest?.(INTERACTIVE_SELECTOR)) return;

      const rect = elementRef.current?.getBoundingClientRect();
      const viewport = currentViewport();
      // jsdom (and a window that has not laid out yet) reports a zero rect, so
      // fall back to where the default corner puts a window of this size.
      const fallback = shellWindowSize(viewport, size);

      const origin =
        position ??
        (rect
          ? { x: rect.left, y: rect.top }
          : {
              x: viewport.width - fallback.width - SHELL_INSET,
              y: viewport.height - fallback.height - SHELL_INSET,
            });

      dragRef.current = {
        originX: origin.x,
        originY: origin.y,
        startX: event.clientX,
        startY: event.clientY,
        pointerId: event.pointerId,
        hasMoved: false,
      };

      // Capture so a fast drag that outruns the header keeps sending moves.
      if (event.pointerId !== undefined) {
        (event.currentTarget as Element | null)?.setPointerCapture?.(
          event.pointerId
        );
      }

      latestRef.current = clampShellPosition(origin, viewport, size);
      setIsPointerDown(true);
    },
    [elementRef, enabled, position, size]
  );

  return {
    dragHandleProps: { onPointerDown },
    dragPosition,
    isDragging: dragPosition !== null,
  };
};

export interface UseResizeSizeOptions {
  /** The window element, used to pin its bottom-right corner for the gesture. */
  elementRef: RefObject<HTMLElement | null>;
  /** The committed size, or null while the window is at its default size. */
  size: ShellSize | null;
  /** The committed position, or null while the window sits at its default. */
  position: ShellPosition | null;
  /**
   * Called once when the gesture ends. The position is null while the window
   * is still at its default corner — CSS anchors it there, so growing it needs
   * no position at all.
   */
  onCommit: (size: ShellSize, position: ShellPosition | null) => void;
  enabled?: boolean;
}

export interface UseResizeSizeResult {
  /** Spread onto the corner handle. */
  resizeHandleProps: { onPointerDown: (event: PointerLike) => void };
  /** The live size during a resize; null when no resize is running. */
  resizeSize: ShellSize | null;
  /** The live position during a resize of an already-dragged window. */
  resizePosition: ShellPosition | null;
  isResizing: boolean;
}

/**
 * Aspect-locked resize from the window's top-left corner (ORA-649). The
 * bottom-right corner stays pinned, so the window grows into the page rather
 * than off the edge it is anchored to, and the same pointer-capture shape as
 * `useDragPosition` keeps a fast gesture from escaping the 16px handle.
 */
export const useResizeSize = ({
  elementRef,
  size,
  position,
  onCommit,
  enabled = true,
}: UseResizeSizeOptions): UseResizeSizeResult => {
  const [live, setLive] = useState<{
    size: ShellSize;
    position: ShellPosition | null;
  } | null>(null);

  const [isPointerDown, setIsPointerDown] = useState(false);

  const resizeRef = useRef<{
    startX: number;
    startY: number;
    baseWidth: number;
    baseHeight: number;
    right: number;
    bottom: number;
    isPositioned: boolean;
    pointerId?: number;
    hasMoved: boolean;
  } | null>(null);

  const latestRef = useRef<{
    size: ShellSize;
    position: ShellPosition | null;
  } | null>(null);

  const onCommitRef = useRef(onCommit);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    if (!isPointerDown) return undefined;

    const handleMove = (event: PointerEvent) => {
      const resize = resizeRef.current;
      if (!resize) return;
      if (
        resize.pointerId !== undefined &&
        event.pointerId !== resize.pointerId
      ) {
        return;
      }

      const dx = event.clientX - resize.startX;
      const dy = event.clientY - resize.startY;

      if (!resize.hasMoved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      resize.hasMoved = true;

      // The handle is the top-left corner, so moving up or left grows the
      // window. The ratio is locked, so the dominant axis wins and the other
      // follows it — dragging along one edge still resizes the whole window.
      const viewport = currentViewport();

      const nextSize = clampShellSize(
        {
          width: Math.max(
            resize.baseWidth - dx,
            (resize.baseHeight - dy) / SHELL_ASPECT_RATIO
          ),
        },
        viewport
      );

      const nextPosition = resize.isPositioned
        ? clampShellPosition(
            {
              x: resize.right - nextSize.width,
              y: resize.bottom - nextSize.height,
            },
            viewport,
            nextSize
          )
        : null;

      latestRef.current = { size: nextSize, position: nextPosition };
      setLive(latestRef.current);
    };

    const handleUp = (event: PointerEvent) => {
      const resize = resizeRef.current;
      if (
        resize?.pointerId !== undefined &&
        event.pointerId !== undefined &&
        event.pointerId !== resize.pointerId
      ) {
        return;
      }

      const committed = resize?.hasMoved ? latestRef.current : null;

      resizeRef.current = null;
      latestRef.current = null;
      setIsPointerDown(false);
      setLive(null);
      if (committed) onCommitRef.current(committed.size, committed.position);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [isPointerDown]);

  const onPointerDown = useCallback(
    (event: PointerLike) => {
      if (!enabled) return;
      if (event.button !== undefined && event.button !== 0) return;

      const viewport = currentViewport();
      const rect = elementRef.current?.getBoundingClientRect();
      const base = clampShellSize(size ?? maxShellSize(viewport), viewport);

      // jsdom (and a window that has not laid out yet) reports a zero rect, so
      // fall back to where the default corner puts the panel.
      const right = rect?.width
        ? rect.right
        : (position?.x ?? viewport.width - base.width - SHELL_INSET) +
          base.width;

      const bottom = rect?.height
        ? rect.bottom
        : (position?.y ?? viewport.height - base.height - SHELL_INSET) +
          base.height;

      resizeRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        baseWidth: base.width,
        baseHeight: base.height,
        right,
        bottom,
        isPositioned: position !== null,
        pointerId: event.pointerId,
        hasMoved: false,
      };

      // Capture so a fast drag that outruns the 16px handle keeps reporting.
      if (event.pointerId !== undefined) {
        (event.currentTarget as Element | null)?.setPointerCapture?.(
          event.pointerId
        );
      }

      event.preventDefault?.();
      latestRef.current = { size: base, position };
      setIsPointerDown(true);
    },
    [elementRef, enabled, position, size]
  );

  return {
    resizeHandleProps: { onPointerDown },
    resizeSize: live?.size ?? null,
    resizePosition: live?.position ?? null,
    isResizing: live !== null,
  };
};
